import qrcode from 'qrcode-generator';

/** The code rule shared with the API and the database CHECK (apps/api/src/short-links). */
export const SHORT_LINK_CODE = /^[a-z0-9][a-z0-9-]{1,39}$/;
export const SHORT_LINK_TARGET = /^\/t\/[a-z0-9][a-z0-9-]{1,39}(?:\/[A-Za-z0-9._~-]+)*$/;

/** The address a printed QR encodes: the permanent short URL, never the destination. */
export function printedUrl(publicBaseUrl: string, code: string): string {
  return `${publicBaseUrl.replace(/\/+$/, '')}/q/${code}`;
}

/** Modules, row-major, `true` = dark. Error correction Q (about 25 % damage tolerance). */
export function qrMatrix(text: string): readonly (readonly boolean[])[] {
  const qr = qrcode(0, 'Q');
  qr.addData(text, 'Byte');
  qr.make();
  const size = qr.getModuleCount();
  return Array.from({ length: size }, (_, r) =>
    Array.from({ length: size }, (_, c) => qr.isDark(r, c)),
  );
}

/** Quiet zone in modules. 4 is the QR specification's minimum and what print needs. */
export const QR_BORDER = 4;

const xmlEscape = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Self-contained SVG with a white background and quiet zone, so it scans on any page colour. */
export function qrSvg(text: string, scale = 10): string {
  const matrix = qrMatrix(text);
  const size = matrix.length + QR_BORDER * 2;
  const rects: string[] = [];
  matrix.forEach((row, r) => {
    let c = 0;
    while (c < row.length) {
      if (!row[c]) {
        c += 1;
        continue;
      }
      let end = c;
      while (end < row.length && row[end]) end += 1;
      rects.push(`M${c + QR_BORDER} ${r + QR_BORDER}h${end - c}v1h-${end - c}z`);
      c = end;
    }
  });
  const px = size * scale;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${px}" height="${px}" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges">` +
    `<title>${xmlEscape(`QR code: ${text}`)}</title>` +
    `<rect width="${size}" height="${size}" fill="#ffffff"/>` +
    `<path d="${rects.join('')}" fill="#000000"/></svg>`
  );
}

/** PNG blob of the same code (browser only: needs a canvas). */
/** 100 px per module → 4100 × 4100 px for the 41-module nashmi code: sharp up to ≈ 35 cm at 300 dpi. */
export const PRINT_PNG_SCALE = 100;

export async function qrPngBlob(text: string, scale = PRINT_PNG_SCALE): Promise<Blob> {
  const matrix = qrMatrix(text);
  const size = matrix.length + QR_BORDER * 2;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size * scale;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('canvas is not available');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000000';
  matrix.forEach((row, r) =>
    row.forEach((dark, c) => {
      if (dark) ctx.fillRect((c + QR_BORDER) * scale, (r + QR_BORDER) * scale, scale, scale);
    }),
  );
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('PNG export failed'))),
      'image/png',
    ),
  );
}

export function suggestedFilename(code: string, ext: 'svg' | 'png'): string {
  return `${code}-qr.${ext}`;
}

type Tone = 'success' | 'warning' | 'danger' | 'default';

export function destinationBadge(state: 'unverified' | 'verified' | 'stale' | 'failed'): {
  tone: Tone;
  label: string;
} {
  switch (state) {
    case 'verified':
      return { tone: 'success', label: 'تأییدشده' };
    case 'failed':
      return { tone: 'danger', label: 'ردشده' };
    case 'stale':
      return { tone: 'warning', label: 'قدیمی (تغییر کرده)' };
    default:
      return { tone: 'default', label: 'بررسی‌نشده' };
  }
}

export function rehearsalBadge(state: 'not_done' | 'passed' | 'failed'): {
  tone: Tone;
  label: string;
} {
  switch (state) {
    case 'passed':
      return { tone: 'success', label: 'آزمون تغییر/بازگردانی: موفق' };
    case 'failed':
      return { tone: 'danger', label: 'آزمون تغییر/بازگردانی: ناموفق' };
    default:
      return { tone: 'default', label: 'آزمون تغییر/بازگردانی: انجام نشده' };
  }
}

/** Print approval is shown only as ready when the server says every gate holds right now. */
export function printBadge(
  state: 'not_approved' | 'approved' | 'stale',
  printReady: boolean,
): { tone: Tone; label: string } {
  if (state === 'approved' && printReady) return { tone: 'success', label: 'تأییدشده برای چاپ' };
  if (state === 'approved') return { tone: 'warning', label: 'تأیید شده ولی مسدود' };
  if (state === 'stale') return { tone: 'warning', label: 'تأیید قدیمی' };
  return { tone: 'default', label: 'تأیید نشده' };
}

/**
 * Client-side hint only (the server is authoritative): an internal `/t/…` path or an `https://` URL.
 * Returns the trimmed value, or `null` if it obviously cannot be accepted.
 */
export function destinationHint(input: string): string | null {
  const value = input.trim();
  if (SHORT_LINK_TARGET.test(value) && !value.includes('..')) return value;
  if (/^https:\/\/[^\s\\#@]+$/i.test(value) && value.length <= 2048) return value;
  return null;
}
